;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { passCacheClientSingleton } from '@/lib/cache/client'
import type { FakeStore, UserFakeDoc } from './types'

// 24 hours. QA sessions are workday-scoped; every putUserDoc (seed and each
// mutation) rewrites doc + index with a fresh TTL, so an active session never
// expires mid-flow, while forgotten fake state cleans itself up instead of
// confusing whoever signs in with that test user next week.
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000

const docKey = (pid: string) => `fakes:memberships:${pid}`
const indexKey = (mvaultId: string) => `fakes:mvault-index:${mvaultId}`

// The subset of CacheClient the store needs. Only plain get/set/del: the
// hash operations are redis-only and would throw under CACHE_MODE=local.
interface MinimalCacheClient {
  get<T = unknown>(key: string): Promise<T | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set(key: string, value: any, ttl: number): Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  del(key: string): Promise<any> | void;
}

/**
 * FakeStore backed by the shared cache client: Redis on staging (state is
 * visible to every ECS container), the in-process Map under CACHE_MODE=local.
 *
 * Mutations are get-modify-put, last-write-wins. That's acceptable for a dev
 * tool where the only writer is the user acting on their own pid; the worst
 * case is a lost update fixed by re-seeding. Deliberately no withLock: it
 * throws on contention, which would turn a harmless race into a 500.
 *
 * Doc and index keys are always written together with the same TTL so they
 * expire in lockstep. If an index entry ever outlives its doc, handlers treat
 * the dangling lookup as "unknown" and fall through, which is safe.
 */
export function createCacheFakeStore(
  opts: { ttlMs?: number; client?: MinimalCacheClient } = {},
): FakeStore {
  const ttlMs = opts.ttlMs ?? DEFAULT_TTL_MS
  const getClient = async () => opts.client ?? await passCacheClientSingleton()

  return {
    async getUserDoc(pid) {
      const client = await getClient()
      const doc = await client.get<UserFakeDoc>(docKey(pid))
      return doc ?? null
    },

    async putUserDoc(pid, doc) {
      const client = await getClient()
      await client.set(docKey(pid), doc, ttlMs)
      for(const membership of doc.memberships) {
        await client.set(indexKey(membership.data.mvault_id), pid, ttlMs)
      }
    },

    async deleteUser(pid) {
      const client = await getClient()
      const doc = await client.get<UserFakeDoc>(docKey(pid))
      if(doc) {
        for(const membership of doc.memberships) {
          await client.del(indexKey(membership.data.mvault_id))
        }
      }
      await client.del(docKey(pid))
    },

    async findPidByMvaultId(mvaultId) {
      const client = await getClient()
      const pid = await client.get<string>(indexKey(mvaultId))
      return pid ?? null
    },
  }
}
